/**
 * Boot screen. Deliberately dependency-free and inlined so it paints on the very
 * first frame, long before Three or the WASM physics blob have parsed.
 */
export interface LoadingHandle {
  progress(frac: number, label: string): void;
  done(): void;
  fail(message: string): void;
}

const LABELS: Record<string, string> = {
  materials: 'Mixing concrete…',
  render: 'Raising the sun…',
  physics: 'Tightening bolts…',
  terrain: 'Grading the ground…',
  world: 'Planting trees…',
  vehicles: 'Fuelling the machines…',
  build: 'Unrolling blueprints…',
  gameplay: 'Printing work orders…',
  audio: 'Testing the horn…',
  fx: 'Kicking up dust…',
  ui: 'Handing out hard hats…',
  // Preload phases (docs/preload-design.md §2.2) — keyed as `phase:<PreloadPhaseId>`.
  'phase:textures': 'Painting the site…',
  'phase:geometry': 'Cutting the timber…',
  'phase:materials': 'Stirring the paint…',
  'phase:physics': 'Checking the brakes…',
  'phase:audio': 'Tuning the engines…',
  'phase:shaders': 'Polishing the paint…',
  ready: 'Ready to build!',
};

/** Dev watchdog: the loading bar must advance at least this often. */
const STALL_WARN_MS = 500;

export function showLoading(mount: HTMLElement): LoadingHandle {
  const el = document.createElement('div');
  el.setAttribute('role', 'status');
  el.setAttribute('aria-live', 'polite');
  el.innerHTML = `
    <style>
      .stg-load{position:fixed;inset:0;z-index:50;display:grid;place-items:center;
        background:radial-gradient(120% 90% at 50% 8%,#2b3648 0%,#141a24 55%,#0b0f16 100%);
        color:#f4f7fb;font-family:system-ui,-apple-system,"Segoe UI",sans-serif;
        transition:opacity .55s ease;}
      .stg-load__inner{text-align:center;width:min(78vw,460px)}
      .stg-load__hat{font-size:64px;line-height:1;filter:drop-shadow(0 8px 18px rgba(0,0,0,.55));
        animation:stg-bob 2.4s ease-in-out infinite}
      @keyframes stg-bob{0%,100%{transform:translateY(0) rotate(-4deg)}50%{transform:translateY(-10px) rotate(4deg)}}
      .stg-load__title{margin:18px 0 4px;font-size:30px;font-weight:800;letter-spacing:-.02em}
      .stg-load__sub{margin:0 0 26px;font-size:14px;opacity:.6;font-weight:500}
      .stg-load__bar{height:12px;border-radius:99px;background:rgba(255,255,255,.09);
        overflow:hidden;box-shadow:inset 0 1px 3px rgba(0,0,0,.45)}
      .stg-load__fill{height:100%;width:0;border-radius:99px;
        background:repeating-linear-gradient(115deg,#f7b733 0 14px,#1c1c1c 14px 28px);
        background-size:200% 100%;transition:width .35s cubic-bezier(.4,0,.2,1);
        animation:stg-march 1.1s linear infinite}
      @keyframes stg-march{to{background-position:-56px 0}}
      .stg-load__label{margin-top:14px;font-size:14px;font-weight:600;min-height:20px;opacity:.85}
      .stg-load--err .stg-load__fill{animation:none;background:#e5484d}
      @media (prefers-reduced-motion:reduce){.stg-load__hat,.stg-load__fill{animation:none}}
    </style>
    <div class="stg-load">
      <div class="stg-load__inner">
        <div class="stg-load__hat">👷</div>
        <h1 class="stg-load__title">Big Builders</h1>
        <p class="stg-load__sub">Construction Site Simulator</p>
        <div class="stg-load__bar"><div class="stg-load__fill"></div></div>
        <div class="stg-load__label">Warming up the engines…</div>
      </div>
    </div>`;
  mount.appendChild(el);

  const root = el.querySelector('.stg-load') as HTMLElement;
  const fill = el.querySelector('.stg-load__fill') as HTMLElement;
  const label = el.querySelector('.stg-load__label') as HTMLElement;

  // Dev watchdog (docs/preload-design.md §2.6): a bar that sits still for more
  // than 500 ms means someone's boot work is not sliced finely enough.
  let lastAdvance = performance.now();
  let warned = false;
  let watchdog = 0;
  if (import.meta.env.DEV) {
    watchdog = window.setInterval(() => {
      const idle = performance.now() - lastAdvance;
      if (idle > STALL_WARN_MS) {
        if (!warned) console.warn(`[loading] progress stalled ${Math.round(idle)} ms at "${label.textContent}" — slice that phase finer`);
        warned = true;
      } else {
        warned = false;
      }
    }, STALL_WARN_MS);
  }
  const stopWatchdog = (): void => { if (watchdog !== 0) { clearInterval(watchdog); watchdog = 0; } };

  return {
    progress(frac, name) {
      lastAdvance = performance.now();
      fill.style.width = `${Math.round(Math.max(0, Math.min(1, frac)) * 100)}%`;
      label.textContent = LABELS[name] ?? `Loading ${name}…`;
    },
    done() {
      stopWatchdog();
      fill.style.width = '100%';
      label.textContent = LABELS.ready;
      root.style.opacity = '0';
      setTimeout(() => el.remove(), 600);
    },
    fail(message) {
      stopWatchdog();
      root.classList.add('stg-load--err');
      fill.style.width = '100%';
      label.textContent = `Could not start: ${message}`;
    },
  };
}
